import React from 'react';
import { usePermissions } from '../providers/PermissionsProvider';

/**
 * PermissionGate Component
 * 
 * DENY-BY-DEFAULT: Hides children unless user has required permission(s).
 * 
 * This is a security-focused component that ensures UI elements are only
 * shown to users who have the necessary permissions.
 * 
 * Usage:
 *   <PermissionGate require="chat:access">
 *     <ChatButton />
 *   </PermissionGate>
 * 
 *   <PermissionGate require={["dataset:read", "records_v2:read"]}>
 *     <ExploreTab />
 *   </PermissionGate>
 * 
 *   <PermissionGate require={["admin:all"]} fallback={<DisabledButton />}>
 *     <AdminPanel />
 *   </PermissionGate>
 */
export const PermissionGate = ({ 
  require,            // Single permission string or array of permissions
  requireAll = false, // If array: true = need all permissions, false = need any
  fallback = null,    // Component to show if permission denied
  showWhileLoading = false, // Whether to show children while permissions are loading
  children 
}) => {
  const { permissions, isPlatformAdmin, loading } = usePermissions();
  
  // DENY-BY-DEFAULT: Hide while loading unless explicitly allowed
  if (loading && !showWhileLoading) {
    return fallback;
  }
  
  // PLATFORM ADMIN BYPASS: Admins have all permissions
  if (isPlatformAdmin) {
    return <>{children}</>;
  }
  
  // Normalize to array
  const requiredPerms = Array.isArray(require) ? require : [require];
  
  // Check if user has required permissions
  const hasAccess = requireAll
    ? requiredPerms.every(p => permissions.includes(p))
    : requiredPerms.some(p => permissions.includes(p));
  
  if (!hasAccess) {
    return fallback;
  }
  
  return <>{children}</>;
};

/**
 * RoleGate Component
 * 
 * DENY-BY-DEFAULT: Hides children unless user has required role(s).
 * 
 * Usage:
 *   <RoleGate require="admin">
 *     <AdminPanel />
 *   </RoleGate>
 * 
 *   <RoleGate require={["org_user", "org_admin"]}>
 *     <CustomerFeature />
 *   </RoleGate>
 */
export const RoleGate = ({ 
  require,
  requireAll = false,
  fallback = null,
  showWhileLoading = false,
  children 
}) => {
  const { roles, isPlatformAdmin, loading } = usePermissions();
  
  // DENY-BY-DEFAULT: Hide while loading
  if (loading && !showWhileLoading) {
    return fallback;
  }
  
  // PLATFORM ADMIN BYPASS: Admins have all roles
  if (isPlatformAdmin) {
    return <>{children}</>;
  }
  
  const requiredRoles = Array.isArray(require) ? require : [require];
  
  const hasAccess = requireAll
    ? requiredRoles.every(r => roles.includes(r))
    : requiredRoles.some(r => roles.includes(r));
  
  if (!hasAccess) {
    return fallback;
  }
  
  return <>{children}</>;
};

/**
 * UserTypeGate Component
 * 
 * DENY-BY-DEFAULT: Hides children unless user matches type.
 * 
 * Usage:
 *   <UserTypeGate type="member">
 *     <OnboardingFlow />
 *   </UserTypeGate>
 * 
 *   <UserTypeGate type="customer">
 *     <ExploreInterface />
 *   </UserTypeGate>
 */
export const UserTypeGate = ({ 
  type,  // 'member', 'customer', or 'admin'
  fallback = null,
  showWhileLoading = false,
  children 
}) => {
  const { roleTypes, userType, isPlatformAdmin, loading } = usePermissions();
  const resolvedRoleTypes = Array.isArray(roleTypes) && roleTypes.length > 0
    ? roleTypes
    : (userType ? [userType] : []);
  
  if (loading && !showWhileLoading) {
    return fallback;
  }
  
  // PLATFORM ADMIN BYPASS: Allow if type is 'admin' and user is platform admin
  if (isPlatformAdmin && type === 'admin') {
    return <>{children}</>;
  }
  
  if (!resolvedRoleTypes.includes(type)) {
    return fallback;
  }
  
  return <>{children}</>;
};
